import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Card,
  DataStatus,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {HTMLAttributes} from 'react';

import {isNotNil} from 'ramda';

import {
  CreateVehicleFinancingTemplateRequestBody,
  ListVehicleFinancingTemplateResponseBody,
  useCloneVehicleFinancingTemplateMutation,
  useDeleteVehicleFinancingTemplateMutation,
  useGetVehicleFinancingTemplateQuery,
  useRestoreVehicleFinancingTemplateMutation,
  useUpdateVehicleFinancingTemplateMutation,
  VehicleFinancingTemplateResponseBody,
} from '@dms/api/sales';
import {
  getVehicleFinancingTemplateStatus,
  getVehicleFinancingTemplateValidityLabel,
  VehicleFinancingTemplateArchiveDialog,
  VehicleFinancingTemplateForm,
  VehicleFinancingTemplateFormHelpers,
  VehicleFinancingTemplateStatus,
} from '@dms/features/vehicle-financing-template';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError} from 'shared/web';

import {FinancingTemplateDraft} from '../types/FinancingTemplateDraft';
import {FinancingTemplateSelection} from '../types/FinancingTemplateSelection';
import {PendingEditor} from '../types/PendingEditor';
import {getTemplateStatusLabel} from '../utils/getTemplateStatusLabel';
import {getTemplateTypeLabel} from '../utils/getTemplateTypeLabel';
import {templateRequestBodyToDraft} from '../utils/templateRequestBodyToDraft';
import {EmbeddedTemplateFormFooter} from './EmbeddedTemplateFormFooter';
import {TemplateCardHeader} from './TemplateCardHeader';

type DateTimeFormatter = ReturnType<typeof useDateTimeFormatter>;

interface ExistingTemplateCardProps {
  template: ListVehicleFinancingTemplateResponseBody;
  templates: ListVehicleFinancingTemplateResponseBody[];
  handleProps?: HTMLAttributes<HTMLDivElement>;
  isReorderingAvailable: boolean;
  isExpanded: boolean;
  hasPendingEditor: boolean;
  onToggle: () => void;
  onCloned: (template: FinancingTemplateSelection) => Promise<void> | void;
  onArchived: () => void;
  onDeleted: () => void;
  onDiscard: () => void;
  onSaved?: (template: FinancingTemplateDraft) => void;
  onPendingChange: (editor: PendingEditor | null) => void;
  onSavingChange?: (isSaving: boolean) => void;
}

export function ExistingTemplateCard(props: ExistingTemplateCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const [
    canCloneTemplate,
    canUpdateTemplate,
    canArchiveTemplate,
    canRestoreTemplate,
    canDeleteTemplate,
  ] = usePermissions({
    permissionKeys: [
      'cloneVehicleFinancingTemplate',
      'updateVehicleFinancingTemplate',
      'archiveVehicleFinancingTemplate',
      'restoreVehicleFinancingTemplate',
      'deleteVehicleFinancingTemplate',
    ],
  });
  const templateQuery = useGetVehicleFinancingTemplateQuery(
    props.isExpanded ? {vehicleFinancingTemplateId: props.template.id} : skipToken
  );
  const [updateTemplate] = useUpdateVehicleFinancingTemplateMutation();
  const [cloneTemplate, cloneStatus] = useCloneVehicleFinancingTemplateMutation();
  const [deleteTemplate, deleteStatus] = useDeleteVehicleFinancingTemplateMutation();
  const [restoreTemplate, restoreStatus] = useRestoreVehicleFinancingTemplateMutation();
  const status = getVehicleFinancingTemplateStatus(props.template);
  const isActionLoading =
    cloneStatus.isLoading || deleteStatus.isLoading || restoreStatus.isLoading;
  const areTemplateActionsDisabled = props.hasPendingEditor || isActionLoading;

  const handleSubmit = async (
    requestBody: CreateVehicleFinancingTemplateRequestBody,
    helpers: VehicleFinancingTemplateFormHelpers
  ) => {
    props.onSavingChange?.(true);

    // The saving report is reference-counted, so an unbalanced increment would leave the editor
    // believing a save is still running for the rest of the page's life. `finally` guarantees the
    // decrement even if a rejection escapes the handlers below.
    try {
      await updateTemplate({
        vehicleFinancingTemplateId: props.template.id,
        updateVehicleFinancingTemplateRequestBody: requestBody,
      })
        .unwrap()
        .then(() => {
          helpers.reset(undefined, {keepValues: true});
          props.onPendingChange(null);
          showNotification.success(
            i18n.t('entity.vehicleFinancingTemplate.notifications.changesAppliedToAssignedVehicles')
          );
          props.onSaved?.(
            templateRequestBodyToDraft(requestBody, {
              id: props.template.id,
              archived: props.template.archived,
              automatic: props.template.automatic,
              automaticPosition: props.template.automaticPosition,
            })
          );
        })
        .catch((error: ApiError) =>
          handleApiError(error, {signalApiError: helpers.blockerContext?.reportApiFailure})
        );
    } finally {
      props.onSavingChange?.(false);
    }
  };

  const handleClone = () => {
    cloneTemplate({vehicleFinancingTemplateId: props.template.id})
      .unwrap()
      .then(async ({id}) => {
        showNotification.success(
          i18n.t('entity.vehicleFinancingTemplate.notifications.successfullyCloned')
        );
        await props.onCloned({id, type: props.template.type});
      })
      .catch(handleApiError);
  };

  const handleArchive = () => {
    openDialog(
      <VehicleFinancingTemplateArchiveDialog
        template={props.template}
        templates={props.templates}
        onArchived={props.onArchived}
      />,
      {
        title: i18n.t('entity.vehicleFinancingTemplate.archiveDialog.title'),
        size: 'small',
      }
    );
  };

  const handleRestore = () => {
    restoreTemplate({vehicleFinancingTemplateId: props.template.id})
      .unwrap()
      .then(() =>
        showNotification.success(
          i18n.t('entity.vehicleFinancingTemplate.notifications.successfullyRestored')
        )
      )
      .catch(handleApiError);
  };

  const handleDelete = () => {
    openConfirmDialog({
      text: i18n.t('general.actions.namedConfirmDeleteQuestion', {
        name: props.template.name,
      }),
      onConfirm: () =>
        deleteTemplate({vehicleFinancingTemplateId: props.template.id})
          .unwrap()
          .then(() => {
            showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
            props.onDeleted();
          })
          .catch(handleApiError),
    });
  };

  const lifecycleAction = match(props.template.archived)
    .with(true, () => ({
      type: 'button' as const,
      title: i18n.t('entity.vehicleFinancingTemplate.actions.restore'),
      'aria-label': i18n.t('entity.vehicleFinancingTemplate.actions.restore'),
      leftIcon: 'content/unarchive' as const,
      variant: 'ghost' as const,
      isDisabled: areTemplateActionsDisabled || !canRestoreTemplate,
      onClick: handleRestore,
    }))
    .with(false, () => ({
      type: 'button' as const,
      title: i18n.t('general.actions.archive'),
      'aria-label': i18n.t('general.actions.archive'),
      leftIcon: 'content/archive' as const,
      variant: 'ghost' as const,
      isDisabled: areTemplateActionsDisabled || !canArchiveTemplate,
      onClick: handleArchive,
    }))
    .exhaustive();
  const actions = [
    {
      type: 'button' as const,
      title: i18n.t('general.actions.duplicate'),
      'aria-label': i18n.t('general.actions.duplicate'),
      leftIcon: 'content/copy' as const,
      variant: 'ghost' as const,
      isDisabled: areTemplateActionsDisabled || !canCloneTemplate,
      onClick: handleClone,
    },
    lifecycleAction,
    {
      type: 'button' as const,
      title: i18n.t('general.actions.delete'),
      'aria-label': i18n.t('general.actions.delete'),
      leftIcon: 'action/delete' as const,
      variant: 'dangerGhost' as const,
      isDisabled: areTemplateActionsDisabled || !canDeleteTemplate,
      onClick: handleDelete,
    },
  ];

  return (
    <Card
      variant="inlineGrey"
      colorStripe={props.isExpanded ? 'blue' : undefined}
      data-testid={`financing-template-${props.template.id}`}
    >
      <TemplateCardHeader
        title={props.template.name}
        titleIcon="action/credit_card"
        flags={[
          {
            label: getTemplateTypeLabel(props.template.type),
            colorScheme: 'blue',
            isSubtle: true,
          },
          {
            label: getTemplateStatusLabel(status),
            colorScheme: getTemplateStatusColor(status),
            isSubtle: true,
          },
        ]}
        parameters={getTemplateParameters({
          template: props.template,
          detail: templateQuery.data,
          formatDateTime,
        })}
        actions={actions satisfies Action[]}
        handleProps={props.handleProps}
        isReorderingAvailable={props.isReorderingAvailable}
        isExpanded={props.isExpanded}
        onToggle={props.onToggle}
        data-testid={`financing-template-${props.template.id}`}
      >
        <DataStatus isLoading={templateQuery.isLoading} isError={templateQuery.isError}>
          <Show when={templateQuery.data}>
            <VehicleFinancingTemplateForm
              key={props.template.id}
              template={templateQuery.data}
              isReadOnly={!canUpdateTemplate || props.template.archived}
              isEmbedded
              isBulkEditEnabled
              onSubmit={handleSubmit}
              renderFooter={(footer) => (
                <EmbeddedTemplateFormFooter
                  footer={footer}
                  onDiscard={() => {
                    footer.discard();
                    props.onDiscard();
                  }}
                />
              )}
              onDirtyChange={props.onPendingChange}
              data-testid={`financing-template-${props.template.id}-form`}
            />
          </Show>
        </DataStatus>
      </TemplateCardHeader>
    </Card>
  );
}

function getTemplateStatusColor(status: VehicleFinancingTemplateStatus) {
  return match(status)
    .with('PLANNED', () => 'yellow' as const)
    .with('ACTIVE', () => 'green' as const)
    .with('EXPIRED', () => 'red' as const)
    .with('ARCHIVED', () => 'neutral' as const)
    .exhaustive();
}

function getTemplateParameters(params: {
  template: ListVehicleFinancingTemplateResponseBody;
  detail: VehicleFinancingTemplateResponseBody | undefined;
  formatDateTime: DateTimeFormatter;
}) {
  const validityLabel = getVehicleFinancingTemplateValidityLabel(params.template, (date) =>
    params.formatDateTime('dateShort', date)
  );
  const scopeLabel = isNotNil(params.detail)
    ? match(params.detail.scope.mode)
        .with('ALL_VEHICLES', () => i18n.t('entity.vehicleFinancingTemplate.scopeMode.allVehicles'))
        .with('BRANCH', () => i18n.t('entity.vehicleFinancingTemplate.scopeMode.branch'))
        .with('SEGMENT', () => i18n.t('entity.vehicleFinancingTemplate.scopeMode.segment'))
        .exhaustive()
    : null;

  return [validityLabel, scopeLabel];
}
